import buildDescriptionSegments, {
  type DescriptionSegment,
  type Translate,
} from "@Modules/billing/Data/Models/InvoiceDescriptionSegments";
import INVOICE_STATUS from "@Constants/invoice-status";
import { currencyFormat, toShortDateTime } from "@Helpers/support";

/**
 * A money amount as serialized by the backend `amount2Money()` helper.
 * The amount arrives as a string in the currency's minor units, for example "1999" for $19.99.
 */
export interface InvoiceMoney {
  amount: string
  currency: string
}

/**
 * One item of an invoice, as grouped by `App\Libs\Billing\DTO\InvoiceItemGroup`.
 */
export interface InvoiceItemEntryData {
  name?: string | null
  description?: string | null
  original_name?: string | null
  interval?: string | null
}

/**
 * The items of an invoice that share a charge type, as grouped by
 * `App\Libs\Billing\DTO\InvoiceItemGroup`.
 */
export interface InvoiceItemGroupData {
  type: number
  label?: string | null
  entries?: InvoiceItemEntryData[]
}

/**
 * Raw invoice row from `App\Http\Resources\Settings\Invoices\InvoiceResource`.
 */
export interface InvoiceData {
  id: string | number
  created_at?: string | null
  status?: string | null
  status_key?: string | null
  display_status?: string | null
  effective_status_key?: string | null
  hosted_invoice_url?: string | null
  items_breakdown?: InvoiceItemGroupData[] | null
  total?: InvoiceMoney | null
  tax?: InvoiceMoney | null
  refunded?: InvoiceMoney | null
  is_payable?: boolean | null
}

/**
 * Invoice smart object with the formatting and predicates the billing page needs.
 */
export interface Invoice extends InvoiceData {
  statusKey: string | null
  effectiveStatusKey: string
  descriptionSegments: DescriptionSegment[]
  downloadUrl: string
  formattedDate: string
  formattedTotal: string
  formattedTax: string
  formattedRefunded: string
  isPaid: () => boolean
  isUnpaid: () => boolean
  isRefunded: () => boolean
  hasReceipt: () => boolean
  hasDescriptionSegments: () => boolean
  hasHostedInvoiceUrl: () => boolean
  hasZeroTotal: () => boolean
  isPayable: () => boolean
}

/**
 * The statuses the backend reports through `effective_status_key`, from
 * `App\Libs\Billing\Stripe\Enums\InvoiceStatus::label()`.
 */
const EFFECTIVE_STATUS = {
  UNPAID: "unpaid",
  REFUNDED: "refunded",
};

/**
 * Formats a money object from the invoice resource, falling back to a zero amount.
 */
function formatMoney(money?: InvoiceMoney | null): string {
  return currencyFormat(Number(money?.amount ?? 0), money?.currency ?? "USD");
}

/**
 * Builds the invoice download URL for the current context.
 * Both routes exist in `routes/web.php`.
 */
function buildDownloadUrl(id: string | number, orgSlug: string | null): string {
  if (orgSlug) {
    return `/app/organization/${orgSlug}/invoice/${id}`;
  }

  return `/app/invoice/${id}`;
}

/**
 * Creates an Invoice smart object from raw server data.
 *
 * @param data The raw invoice row from the server
 * @param orgSlug The organization slug when in org context, null in single-user context
 * @param translate The translator the description segments are worded with
 */
export default function InvoiceModel(
  data: InvoiceData,
  orgSlug: string | null,
  translate: Translate,
): Invoice {
  const statusKey: string | null = data.status_key ?? data.status?.toLowerCase() ?? null;
  const effectiveStatusKey: string = data.effective_status_key ?? data.display_status?.toLowerCase() ?? "";
  const descriptionSegments: DescriptionSegment[] = buildDescriptionSegments(data.items_breakdown, translate);

  const that: Invoice = {
    ...data,
    statusKey,
    effectiveStatusKey,
    descriptionSegments,
    downloadUrl: buildDownloadUrl(data.id, orgSlug),
    formattedDate: data.created_at ? toShortDateTime(data.created_at) : "",
    formattedTotal: formatMoney(data.total),
    formattedTax: formatMoney(data.tax),
    formattedRefunded: formatMoney(data.refunded),

    /**
     * Returns true when the invoice has been paid.
     */
    isPaid(): boolean {
      return statusKey === INVOICE_STATUS.PAID;
    },

    /**
     * Returns true when the invoice is still to be settled, whether the provider or our own
     * bookkeeping says so.
     */
    isUnpaid(): boolean {
      return effectiveStatusKey === EFFECTIVE_STATUS.UNPAID;
    },

    /**
     * Returns true when the invoice was refunded.
     */
    isRefunded(): boolean {
      return effectiveStatusKey === EFFECTIVE_STATUS.REFUNDED;
    },

    /**
     * Returns true when money has moved, which is what a receipt documents.
     */
    hasReceipt(): boolean {
      return that.isPaid() || that.isRefunded();
    },

    /**
     * Returns true when the invoice says what it charged for.
     */
    hasDescriptionSegments(): boolean {
      return descriptionSegments.length > 0;
    },

    /**
     * Returns true when the provider hosts an invoice page of its own for this invoice.
     */
    hasHostedInvoiceUrl(): boolean {
      return Boolean(data.hosted_invoice_url);
    },

    /**
     * Returns true when the invoice total is zero, which means it is not downloadable.
     */
    hasZeroTotal(): boolean {
      return data.total?.amount === "0";
    },

    /**
     * Returns true when the server says the invoice can be paid right now.
     * Anything other than an explicit true leaves the invoice unpayable.
     */
    isPayable(): boolean {
      return data.is_payable === true;
    },
  };

  return that;
}
